"use client";

import React, { useState } from "react";
import { Input, Textarea } from "rizzui";
import { Button } from "rizzui";
import toast from "react-hot-toast";
import homepageApi from "@/services/homepageApi";
import logger from "@/utils/logger";
import {
  PiPalette,
  PiTextT,
  PiImage,
  PiGear,
  PiPlus,
  PiTrash,
  PiCaretUp,
  PiCaretDown,
  PiCheck,
  PiUploadSimple,
} from "react-icons/pi";

interface ColorEditorProps {
  label: string;
  value?: string;
  onChange: (value: string) => void;
}

export function ColorEditor({ label, value, onChange }: ColorEditorProps) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs font-medium text-gray-600 w-24">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 rounded cursor-pointer border-0"
        />
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="!h-8 !text-xs w-28"
          placeholder="#000000"
        />
      </div>
    </div>
  );
}

interface GradientEditorProps {
  label: string;
  primary?: string;
  secondary?: string;
  onPrimaryChange: (value: string) => void;
  onSecondaryChange: (value: string) => void;
}

export function GradientEditor({
  label,
  primary,
  secondary,
  onPrimaryChange,
  onSecondaryChange,
}: GradientEditorProps) {
  const [enabled, setEnabled] = useState(!!primary && !!secondary);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setEnabled(!enabled)}
          className={`w-5 h-5 rounded flex items-center justify-center text-xs ${
            enabled ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-400"
          }`}
        >
          {enabled && <PiCheck className="w-3 h-3" />}
        </button>
        <span className="text-xs font-medium text-gray-600">{label}</span>
      </div>
      {enabled && (
        <div className="pl-7 space-y-2">
          <ColorEditor label="Primary" value={primary} onChange={onPrimaryChange} />
          <ColorEditor label="Secondary" value={secondary} onChange={onSecondaryChange} />
          <div
            className="h-10 rounded-lg mt-2"
            style={{
              background: primary && secondary ? `linear-gradient(135deg, ${primary}, ${secondary})` : "#ccc",
            }}
          />
        </div>
      )}
    </div>
  );
}

interface TextEditorProps {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
}

export function TextEditor({ label, value, onChange, placeholder, multiline }: TextEditorProps) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-gray-600">{label}</label>
      {multiline ? (
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="!text-xs"
          rows={3}
        />
      ) : (
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="!h-8 !text-xs"
        />
      )}
    </div>
  );
}

interface ImageEditorProps {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function ImageEditor({ label, value, onChange, placeholder }: ImageEditorProps) {
  return (
    <div className="space-y-1.5 border border-gray-100 p-2.5 rounded-lg bg-gray-50/50">
      <label className="text-xs font-semibold text-gray-700 block">{label}</label>
      
      {/* Thumbnail Preview */}
      {value && (
        <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-white">
          <img
            src={value}
            alt="Preview"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="flex flex-col gap-2">
        {/* Upload Button */}
        <label className="flex items-center justify-center gap-1.5 py-1.5 px-3 bg-white border border-dashed border-gray-300 rounded-md text-xs font-semibold text-gray-700 hover:bg-slate-100 cursor-pointer transition-all">
          <PiUploadSimple className="w-4 h-4 text-gray-500" />
          <span>Upload Image File</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) {
                const toastId = toast.loading("Uploading image to server...");
                try {
                  const response = await homepageApi.uploadImage(file);
                  const url = response.data?.url || response.url;
                  if (url) {
                    onChange(url);
                    toast.success("Image uploaded successfully!", { id: toastId });
                  } else {
                    toast.error("Failed to get uploaded path.", { id: toastId });
                  }
                } catch (error) {
                  toast.error("Error uploading image to server.", { id: toastId });
                  logger.error(error);
                }
              }
            }}
          />
        </label>

        {/* Input box fallback */}
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "Or enter image path..."}
          className="!h-7 !text-[11px]"
        />
      </div>
    </div>
  );
}

interface ListItemEditorProps {
  label: string;
  items: Record<string, any>[];
  itemSchema: {
    key: string;
    label: string;
    type: "text" | "textarea" | "image" | "select";
    options?: { value: string; label: string }[];
  }[];
  onChange: (items: Record<string, any>[]) => void;
  addItemText?: string;
}

export function ListItemEditor({
  label,
  items,
  itemSchema,
  onChange,
  addItemText = "Add Item",
}: ListItemEditorProps) {
  const updateItem = (idx: number, field: string, value: any) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    onChange(newItems);
  };

  const removeItem = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const addItem = () => {
    const newItem: Record<string, any> = {};
    itemSchema.forEach(({ key }) => {
      newItem[key] = key === "image" ? "/assets/img/placeholder.png" : "";
    });
    onChange([...items, newItem]);
  };

  const moveItem = (idx: number, direction: "up" | "down") => {
    const newItems = [...items];
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= items.length) return;
    [newItems[idx], newItems[swapIdx]] = [newItems[swapIdx], newItems[idx]];
    onChange(newItems);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-gray-600">{label}</label>
        <Button size="sm" variant="outline" onClick={addItem}>
          <PiPlus className="w-3 h-3 mr-1" />
          {addItemText}
        </Button>
      </div>
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg border border-gray-100"
          >
            <div className="flex flex-col gap-2 flex-1">
              {itemSchema.map(({ key, label: fieldLabel, type, options }) => (
                <div key={key} className="flex items-start gap-2">
                  <span className="text-[10px] text-gray-500 w-16 pt-1.5">{fieldLabel}</span>
                  {type === "image" ? (
                    <div className="flex items-center gap-1.5 flex-1">
                      {item[key] && (
                        <div className="relative w-8 h-8 rounded border border-gray-200 overflow-hidden bg-white flex-shrink-0">
                          <img src={item[key]} alt="" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <input
                        type="text"
                        value={item[key] || ""}
                        onChange={(e) => updateItem(idx, key, e.target.value)}
                        className="h-7 text-[11px] px-2 py-1 flex-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                        placeholder="/assets/img/..."
                      />
                      <label className="flex items-center justify-center p-1.5 bg-white border border-dashed border-gray-300 rounded hover:bg-slate-50 cursor-pointer transition-all border-solid flex-shrink-0">
                        <PiUploadSimple className="w-3.5 h-3.5 text-gray-500" />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const toastId = toast.loading("Uploading list image...");
                              try {
                                const response = await homepageApi.uploadImage(file);
                                const url = response.data?.url || response.url;
                                if (url) {
                                  updateItem(idx, key, url);
                                  toast.success("Image uploaded!", { id: toastId });
                                } else {
                                  toast.error("Failed to get uploaded path.", { id: toastId });
                                }
                              } catch (error) {
                                toast.error("Error uploading image.", { id: toastId });
                                logger.error(error);
                              }
                            }
                          }}
                        />
                      </label>
                    </div>
                  ) : type === "textarea" ? (
                    <textarea
                      value={item[key] || ""}
                      onChange={(e) => updateItem(idx, key, e.target.value)}
                      className="w-full text-[11px] px-2 py-1.5 flex-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white resize-y font-sans leading-normal min-h-[52px]"
                      rows={2}
                    />
                  ) : type === "select" ? (
                    <select
                      value={item[key] || ""}
                      onChange={(e) => updateItem(idx, key, e.target.value)}
                      className="h-7 text-[11px] px-2 py-0.5 flex-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                    >
                      {(options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={item[key] || ""}
                      onChange={(e) => updateItem(idx, key, e.target.value)}
                      className="h-7 text-[11px] px-2 py-1 flex-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-1">
              <button
                onClick={() => moveItem(idx, "up")}
                className="p-1 text-gray-400 hover:text-gray-600"
                disabled={idx === 0}
              >
                <PiCaretUp className="w-3 h-3" />
              </button>
              <button
                onClick={() => moveItem(idx, "down")}
                className="p-1 text-gray-400 hover:text-gray-600"
                disabled={idx === items.length - 1}
              >
                <PiCaretDown className="w-3 h-3" />
              </button>
              <button
                onClick={() => removeItem(idx)}
                className="p-1 text-red-400 hover:text-red-600"
              >
                <PiTrash className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface ToggleEditorProps {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

export function ToggleEditor({ label, value, onChange }: ToggleEditorProps) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <button
        onClick={() => onChange(!value)}
        className={`w-10 h-5 rounded-full transition-colors relative ${
          value ? "bg-blue-500" : "bg-gray-300"
        }`}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
            value ? "left-5" : "left-0.5"
          }`}
        />
      </button>
    </div>
  );
}

interface FontEditorProps {
  label: string;
  value?: string;
  onChange: (value: string) => void;
}

const FONT_OPTIONS = [
  { value: "'Inter', sans-serif", label: "Inter" },
  { value: "'Poppins', sans-serif", label: "Poppins" },
  { value: "'Roboto', sans-serif", label: "Roboto" },
  { value: "'Open Sans', sans-serif", label: "Open Sans" },
  { value: "'Manrope', sans-serif", label: "Manrope" },
  { value: "'Work Sans', sans-serif", label: "Work Sans" },
  { value: "'Plus Jakarta Sans', sans-serif", label: "Plus Jakarta Sans" },
  { value: "system-ui, sans-serif", label: "System UI" },
];

export function FontEditor({ label, value, onChange }: FontEditorProps) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-gray-600">{label}</label>
      <select
        value={value || "'Inter', sans-serif"}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-8 px-2 text-xs border border-gray-200 rounded-lg bg-white"
      >
        {FONT_OPTIONS.map((font) => (
          <option key={font.value} value={font.value}>
            {font.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface BackgroundEditorProps {
  label: string;
  value?: string;
  onChange: (value: string) => void;
}

export function BackgroundEditor({ label, value, onChange }: BackgroundEditorProps) {
  return (
    <div className="space-y-1.5 border border-gray-100 p-2.5 rounded-lg bg-gray-50/50">
      <label className="text-xs font-semibold text-gray-700 block">{label}</label>

      {/* Background Preview */}
      {value && (
        <div
          className="h-16 rounded-lg bg-cover bg-center border border-gray-200"
          style={{ backgroundImage: `url(${value})` }}
        />
      )}

      <div className="flex flex-col gap-2">
        <label className="flex items-center justify-center gap-1.5 py-1.5 px-3 bg-white border border-dashed border-gray-300 rounded-md text-xs font-semibold text-gray-700 hover:bg-slate-100 cursor-pointer transition-all">
          <PiUploadSimple className="w-4 h-4 text-gray-500" />
          <span>Upload Background File</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) {
                const toastId = toast.loading("Uploading background to server...");
                try {
                  const response = await homepageApi.uploadImage(file);
                  const url = response.data?.url || response.url;
                  if (url) {
                    onChange(url);
                    toast.success("Background uploaded successfully!", { id: toastId });
                  } else {
                    toast.error("Failed to get uploaded path.", { id: toastId });
                  }
                } catch (error) {
                  toast.error("Error uploading background to server.", { id: toastId });
                  logger.error(error);
                }
              }
            }}
          />
        </label>
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Or enter background path..."
          className="!h-7 !text-[11px]"
        />
      </div>
    </div>
  );
}

interface SectionTabProps {
  activeTab: "content" | "style";
  onTabChange: (tab: "content" | "style") => void;
}

export function SectionTabs({ activeTab, onTabChange }: SectionTabProps) {
  return (
    <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
      <button
        onClick={() => onTabChange("content")}
        className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${
          activeTab === "content"
            ? "bg-white text-gray-900 shadow-sm"
            : "text-gray-600 hover:text-gray-900"
        }`}
      >
        Content
      </button>
      <button
        onClick={() => onTabChange("style")}
        className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${
          activeTab === "style"
            ? "bg-white text-gray-900 shadow-sm"
            : "text-gray-600 hover:text-gray-900"
        }`}
      >
        Style
      </button>
    </div>
  );
}

interface EditSectionProps {
  content: Record<string, any>;
  onContentChange: (content: Record<string, any>) => void;
  contentFields?: React.ReactNode;
  styleFields?: React.ReactNode;
}

export default function EditSection({
  content,
  onContentChange,
  contentFields,
  styleFields,
}: EditSectionProps) {
  const [activeTab, setActiveTab] = useState<"content" | "style">("content");

  const updateField = (field: string, value: any) => {
    onContentChange({ ...content, [field]: value });
  };

  const updateColorPalette = (field: "primary" | "secondary", value: string) => {
    onContentChange({
      ...content,
      colorPalette: {
        ...content.colorPalette,
        [field]: value,
      },
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-100">
        <SectionTabs activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === "content" ? (
          <div className="space-y-4">{contentFields}</div>
        ) : (
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg space-y-3">
              <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                <PiPalette className="w-4 h-4" />
                Colors
              </h4>
              <GradientEditor
                label="Gradient"
                primary={content.colorPalette?.primary}
                secondary={content.colorPalette?.secondary}
                onPrimaryChange={(v) => updateColorPalette("primary", v)}
                onSecondaryChange={(v) => updateColorPalette("secondary", v)}
              />
            </div>

            <div className="p-3 bg-gray-50 rounded-lg space-y-3">
              <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                <PiTextT className="w-4 h-4" />
                Typography
              </h4>
              <FontEditor
                label="Font Family"
                value={content.fontFamily}
                onChange={(v) => updateField("fontFamily", v)}
              />
            </div>

            <div className="p-3 bg-gray-50 rounded-lg space-y-3">
              <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                <PiImage className="w-4 h-4" />
                Background
              </h4>
              <BackgroundEditor
                label="Background Image"
                value={content.backgroundImage}
                onChange={(v) => updateField("backgroundImage", v)}
              />
            </div>

            {styleFields}
          </div>
        )}
      </div>
    </div>
  );
}
